import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Heritage', href: '#craftsmanship', type: 'scroll' },
    { label: 'Collection', href: '#collection', type: 'scroll' },
    { label: 'Suits', href: '#suit-collection', type: 'scroll' },
    { label: 'Shop', href: '#ready-to-buy', type: 'scroll' },
    { label: 'Craft Gallery', href: '/design-studio', type: 'link' },
    { label: 'Bespoke', href: '#bespoke', type: 'scroll' },
    { label: 'About', href: '/about', type: 'link' },
    { label: 'Contact', href: '/contact', type: 'link' },
  ];

  const handleNavClick = (e, item) => {
    if (item.type === 'scroll') {
      e.preventDefault();
      if (isHomePage) {
        const element = document.querySelector(item.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        window.location.href = '/' + item.href;
      }
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav style={{
        ...styles.nav,
        backgroundColor: scrolled ? 'rgba(255,254,242,0.98)' : 'transparent',
        borderBottom: scrolled ? '1px solid var(--border-light)' : '1px solid transparent',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
      }}>
        <div className="luxury-container" style={styles.navContainer}>
          <Link to="/" style={styles.logo} data-testid="nav-logo">
            <img
              src="/logo.png"
              alt="Silk & Sentiment"
              style={styles.logoImage}
            />
          </Link>

          {/* Desktop Menu */}
          <ul style={styles.desktopMenu}>
            {navItems.map((item, index) => (
              <li key={index} style={styles.navItem}>
                {item.type === 'link' ? (
                  <Link
                    to={item.href}
                    style={styles.navLink}
                    className="navigation-text"
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item)}
                    style={styles.navLink}
                    className="navigation-text"
                  >
                    {item.label}
                  </a>
                )}
              </li>
            ))}
          </ul>

          {/* Mobile Menu Toggle */}
          <button
            style={styles.mobileMenuToggle}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X size={24} color="var(--text-primary)" />
            ) : (
              <Menu size={24} color="var(--text-primary)" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div style={styles.mobileMenu}>
          <ul style={styles.mobileMenuList}>
            {navItems.map((item, index) => (
              <li key={index} style={styles.mobileMenuItem}>
                {item.type === 'link' ? (
                  <Link
                    to={item.href}
                    style={styles.mobileNavLink}
                    className="heading-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item)}
                    style={styles.mobileNavLink}
                    className="heading-3"
                  >
                    {item.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

const styles = {
  nav: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    padding: '20px 0',
    transition: 'all 0.3s ease',
  },
  navContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    textDecoration: 'none',
    color: 'var(--text-primary)',
    display: 'inline-flex',
    alignItems: 'center',
  },
  logoImage: {
    height: '72px',
    width: 'auto',
    display: 'block',
  },
  desktopMenu: {
    display: 'flex',
    listStyle: 'none',
    margin: 0,
    padding: 0,
    gap: '40px',
  },
  navItem: {
    position: 'relative',
  },
  navLink: {
    textDecoration: 'none',
    padding: '8px 0',
    display: 'block',
  },
  mobileMenuToggle: {
    display: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
  },
  mobileMenu: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--bg-primary)',
    zIndex: 99,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileMenuList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    textAlign: 'center',
  },
  mobileMenuItem: {
    marginBottom: '32px',
  },
  mobileNavLink: {
    textDecoration: 'none',
    color: 'var(--text-primary)',
  },
};

// Media query styles
const mediaStyleSheet = document.createElement('style');
mediaStyleSheet.textContent = `
  @media (max-width: 768px) {
    nav > div > ul {
      display: none !important;
    }
    nav > div > button {
      display: block !important;
    }
  }
`;
document.head.appendChild(mediaStyleSheet);

export default Navigation;
